/**
 * COP Alerts Service
 * Fetches SOS messages and emergency alerts
 */

export interface SOSMessage {
  id: number;
  deviceid: string;
  entity_id?: string;
  entity_type?: string;
  course_id?: string;
  device_type?: string;
  comms_direction?: string;
  sms_message: string;
  sms_number?: string;
  sms_timestamp: string;
  timestamp: string;
  device_reference_id?: number;
  latitude?: number;
  longitude?: number;
  geocode_location_id?: number;
  neighborhood_id?: number;
  acknowledged?: boolean;
  acknowledged_by?: string;
  acknowledged_at?: string;
}

export interface SOSResponse {
  messages: SOSMessage[];
  count: number;
  unacknowledged_count: number;
}

class COPAlertsService {
  private baseUrl = '/api';
  private pollingInterval: NodeJS.Timeout | null = null;
  private lastMessageId: number = 0;

  constructor() {
    const stored = localStorage.getItem('sos_last_message_id');
    if (stored) this.lastMessageId = parseInt(stored, 10) || 0;
  }

  private persistLastMessageId() {
    localStorage.setItem('sos_last_message_id', this.lastMessageId.toString());
  }

  /**
   * Fetch all SOS messages
   */
  async getSOSMessages(
    courseId?: string,
    hours: number = 24,
    limit: number = 100
  ): Promise<SOSResponse> {
    const queryParams = new URLSearchParams();
    
    if (courseId && courseId !== 'All Courses') {
      queryParams.append('course_id', courseId);
    }
    
    queryParams.append('hours', hours.toString());
    queryParams.append('limit', limit.toString());

    const url = `${this.baseUrl}/cop-alerts/sos${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Update last message ID and persist
      if (data.messages.length > 0) {
        this.lastMessageId = Math.max(...data.messages.map((m: SOSMessage) => m.id));
        this.persistLastMessageId();
      }
      
      return data;
    } catch (error) {
      console.error('[COP Alerts Service] Error fetching SOS messages:', error);
      throw error;
    }
  }

  /**
   * Check for new SOS messages since last check
   */
  async getRecentSOS(sinceId?: number): Promise<SOSResponse> {
    const queryParams = new URLSearchParams();
    
    const idToCheck = sinceId !== undefined ? sinceId : this.lastMessageId;
    
    if (idToCheck > 0) {
      queryParams.append('since_id', idToCheck.toString());
    }

    const url = `${this.baseUrl}/cop-alerts/sos/recent${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Update last message ID and persist
      if (data.messages.length > 0) {
        this.lastMessageId = Math.max(...data.messages.map((m: SOSMessage) => m.id));
        this.persistLastMessageId();
      }
      
      return data;
    } catch (error) {
      console.error('[COP Alerts Service] Error checking recent SOS:', error);
      throw error;
    }
  }

  /**
   * Start polling for new SOS messages
   */
  startPolling(onNewSOS: (messages: SOSMessage[]) => void, intervalMs: number = 10000) {
    
    this.stopPolling(); // Clear any existing interval
    
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 3;
    
    this.pollingInterval = setInterval(async () => {
      try {
        const response = await this.getRecentSOS();
        consecutiveErrors = 0; // Reset error count on success
        
        if (response.messages.length > 0) {
          onNewSOS(response.messages);
        }
      } catch (error: any) {
        consecutiveErrors++;
        
        if (consecutiveErrors === MAX_CONSECUTIVE_ERRORS) {
          this.stopPolling();
        }
      }
    }, intervalMs);
  }

  /**
   * Stop polling
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Reset last message ID (for testing)
   */
  resetLastMessageId() {
    this.lastMessageId = 0;
  }

  /**
   * Acknowledge an SOS message
   */
  async acknowledgeSOS(messageId: number): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/cop-alerts/sos/${messageId}/acknowledge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.success || true;
    } catch (error) {
      console.error('[COP Alerts Service] Error acknowledging SOS:', error);
      throw error;
    }
  }
}

export const copAlertsService = new COPAlertsService();

